import { Calendar, User, MoreHorizontal, ArrowRight, Download, History } from 'lucide-react';
import { Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@nextui-org/react';
import { useMemo } from 'react';
import { StepProgress } from './StepProgress';
import { VendorDetailRiskBadge } from '../atoms/VendorDetailRiskBadge';
import type { VendorProduct } from '../../types';
import { PRODUCT_ACTIONS } from '../../constants/product-actions';
import type { ProductActionType } from '../../types/product-actions.types';

interface ProductCardProps {
  product: VendorProduct;
  assigneeName: string;
  onViewAssessment?: (productId: string) => void;
  onAction?: (action: ProductActionType, product: VendorProduct) => void;
  className?: string;
}

/**
 * Maps API risk_rating string to a valid RiskLevel for styling
 */
const mapRiskRating = (riskRating: string | null | undefined): 'Low' | 'Medium' | 'High' | null => {
  if (!riskRating) return null;
  const normalized = riskRating.trim();
  if (normalized === 'Low' || normalized === 'Medium' || normalized === 'High') {
    return normalized;
  }
  return null;
};

export const ProductCard = ({
  product,
  assigneeName,
  onViewAssessment,
  onAction,
  className = '',
}: ProductCardProps) => {
  const riskLevel = mapRiskRating(product.risk_rating);

  const iconMap: Record<string, React.FC<{ className?: string }>> = {
    ArrowRight,
    Download,
    History,
  };

  const disabledKeys = useMemo(() => {
    const keys: string[] = [];

    // Disable downloads if assessment hasn't reached Review Results step (total_steps - 1)
    // Report becomes available at step 4 (Review Results), not step 5 (Completed)
    if (product.assessment_step < (product.total_assessment_steps - 1)) {
      keys.push('download_report', 'download_summary');
    }

    return keys;
  }, [product.assessment_step, product.total_assessment_steps]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div
      className={`flex flex-col items-flex-start p-4 gap-4 bg-[#F8FAFC] rounded-lg ${className}`}
      role="article"
      aria-label={`Product: ${product.product_name}`}
    >
      {/* Row 1: Product Name + Badge | Date | User | Buttons (all inline) */}
      <div className="flex flex-row items-center p-0 gap-3 w-full h-6">
        {/* Product Name + Badge */}
        <div className="flex flex-row items-center p-0 gap-3 flex-1 h-5">
          <h3 className="font-['Inter'] font-medium text-sm leading-5 text-[#314158]">
            {product.product_name}
          </h3>
          {riskLevel && <VendorDetailRiskBadge risk={riskLevel} showRiskLabel={false} />}
        </div>

        {/* Updated Date */}
        <div className="flex flex-row items-center p-0 gap-1.5 flex-1 h-4">
          <Calendar size={12} className="text-[#45556C]" />
          <span className="font-['Inter'] font-normal text-xs leading-4 text-[#45556C]">
            Updated {formatDate(product.updated_at)}
          </span>
        </div>

        {/* Assignee - Always show, with 'Unknown' as fallback */}
        <div className="flex flex-row items-center p-0 gap-1.5 flex-1 h-4">
          <User size={12} className="text-[#45556C]" />
          <span className="font-['Inter'] font-normal text-xs leading-4 text-[#45556C]">
            {assigneeName}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-row justify-end items-center p-0 gap-2 w-[148px] h-6">
          <Button
            size="sm"
            variant="bordered"
            onPress={() => onViewAssessment?.(product.id)}
            className="flex flex-row items-center p-1 px-2 gap-1.5 w-[118px] h-6 bg-white border border-[#006FEE] rounded-md font-['Inter'] font-medium text-xs leading-4 text-[#006FEE]"
            aria-label={`View assessment for ${product.product_name}`}
          >
            View Assessment
          </Button>

          {onAction && (
            <Dropdown
              classNames={{
                content: "p-1 min-w-[188px] bg-white border border-[#E2E8F0] rounded-md shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)]",
              }}
            >
              <DropdownTrigger>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className="min-w-5 w-5 h-5"
                  aria-label="More actions"
                >
                  <MoreHorizontal size={20} className="text-[#45556C]" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Product actions"
                disabledKeys={disabledKeys}
                onAction={(key) => onAction(key as ProductActionType, product)}
                classNames={{
                  base: "p-0",
                  list: "gap-0",
                }}
                itemClasses={{
                  base: "p-2 gap-2 h-9 rounded data-[hover=true]:bg-[#F1F5F9]",
                  title: "font-['Inter'] font-normal text-sm leading-5 text-[#314158]",
                }}
              >
                {PRODUCT_ACTIONS.map(({ key, label, icon }) => {
                  const Icon = iconMap[icon];
                  return (
                    <DropdownItem
                      key={key}
                      startContent={Icon ? <Icon className="h-4 w-4 text-[#62748E]" /> : null}
                    >
                      {label}
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
      </div>

      {/* Row 2 & 3: Progress */}
      <StepProgress
        currentStep={product.assessment_step}
        totalSteps={product.total_assessment_steps}
        status={product.status}
      />
    </div>
  );
};
